import { AxiosRequestConfig } from 'axios';
import { useState } from 'react';
import { useCustomerUnauthorizedMessages } from '../../../hooks/useCustomerUnauthorizedMessages';
import useHandleApiError from '../../../hooks/useHandleApiError';
import LangEnum from '../../../lang/LangEnum';
import { BRAND_ID_HEADER_KEY } from '../../../services/api';
import { copyOrder } from '../../../services/modules/orders/orders.repository';
import snackbarStore from '../../../stores/Snackbar.store';
import { useTranslation } from '../../translation/TranslationContext';
import { useOrderActionsContext } from '../contexts/OrderActions.context';
import { OnConfirmData } from '../copy-order/types/copyOrderModal.types';
import { IUseCopyOrder } from '../interfaces/orderCopy.interface';

const { handleMessage } = snackbarStore;

export const useCopyOrder = ({ onClose, reload }: IUseCopyOrder) => {
  const { translate } = useTranslation();
  const { getApiErrorMessage } = useHandleApiError();
  const { showUnauthorizedToCopyOrderProducts, showUnauthorizedOrderProducts } =
    useCustomerUnauthorizedMessages();

  const { orderRaw } = useOrderActionsContext();

  const orderId = orderRaw?.orderId;
  const brandId = orderRaw?.brandId;
  const [invalidCustomerIds, setInvalidCustomerIds] = useState<string[]>([]);

  const handleCopyErrorWithInvalidCustomers = (
    selectedCustomersCount: number,
    invalidIds?: string[],
  ) => {
    if (!invalidIds || invalidIds.length === 0) {
      return;
    }

    setInvalidCustomerIds(invalidIds);

    if (selectedCustomersCount > 1) {
      showUnauthorizedToCopyOrderProducts();
    } else {
      showUnauthorizedOrderProducts();
    }
  };

  const onCopyOrder = async (data: OnConfirmData): Promise<boolean> => {
    let axiosConfig: AxiosRequestConfig = {};
    if (brandId) {
      axiosConfig = { headers: { [BRAND_ID_HEADER_KEY]: brandId } };
    }

    const response = await copyOrder(data, axiosConfig, orderId);
    if (response.ok) {
      handleMessage({ type: 'success', message: translate(LangEnum.ORDER_COPY_SUCCESS_MESSAGE) });
      setInvalidCustomerIds([]);
      if (reload) {
        reload();
      }
      onClose();
      return true;
    }

    const code = response?.data?.code;
    if (code === LangEnum.API_EXCEPTION_CUSTOMER_UNAUTHORIZED_TO_COPY_ORDER_PRODUCTS) {
      const invalidIds = response?.data?.data?.invalidCustomerIds;
      handleCopyErrorWithInvalidCustomers(data.customerGroups?.length || 0, invalidIds);
    }

    const reason = getApiErrorMessage(response, '-');

    handleMessage({
      type: 'error',
      message: translate(LangEnum.ORDER_COPY_ERROR_MESSAGE, { reason }),
    });
    return false;
  };

  return {
    onCopyOrder,
    brandId,
    orderId,
    invalidCustomerIds,
  };
};
